<template>
  <div class="tree-node">
    <div
      class="tree-header"
      :class="{ active: isActive(node) }"
      :style="{ paddingLeft: `${level * 1 + 0.5}rem` }"
      @click.stop="onClick(node)"
      role="button"
      :aria-expanded="isOpen(node.id)"
    >
      <div v-if="hasChildren" class="icon-wrapper">
        <div class="icon" :class="{ 'open': isOpen(node.id) }">
          <div class="icon-line horizontal"></div>
          <div class="icon-line vertical"></div>
        </div>
      </div>

      <div class="node-main">
        <div class="node-title">{{ displayText }}</div>
        <div v-if="subtitle" class="node-sub">{{ subtitle }}</div>
      </div>
    </div>

    <transition @enter="enter" @leave="leave">
      <div v-show="isOpen(node.id)" class="children-wrap">
        <TreeNode
          v-for="(child, i) in children"
          :key="child.id || child.title || i"
          :node="child"
          :level="level + 1"
          :is-open="isOpen"
          :toggle="toggle"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import TreeNode from './TreeNode.vue'

export default {
  name: 'TreeNode',
  components: { TreeNode },
  props: {
    node: { type: Object, required: true },
    level: { type: Number, default: 0 },
    isOpen: { type: Function, required: true },
    toggle: { type: Function, required: true }
  },
  computed: {
    // canonical active element из Vuex
    activeElement() {
      return this.$store.getters['activeElement/activeElement']
    },

    children() {
      const n = this.node || {}
      if (n.children && Array.isArray(n.children.data)) return n.children.data
      if (Array.isArray(n.data)) return n.data
      return []
    },
    hasChildren() {
      return this.children.length > 0
    },
    displayText() {
      const n = this.node || {}
      const d = n.data
      if (typeof d === 'string' && d.trim().length) return d.trim()
      if (typeof d === 'number') return String(d)
      return n.title || n.name || n.id || ''
    },
    subtitle() {
      const n = this.node || {}
      const parts = []
      if (n.tag) parts.push(`<${n.tag}>`)
      if (n.classes && Array.isArray(n.classes) && n.classes.length) {
        parts.push(`${n.classes.length} дчр.`)
      }
      return parts.join(' · ')
    }
  },
  methods: {
    onClick(node) {
      // Сначала раскрытие/сворачивание (если есть дети)
      if (this.hasChildren) {
        this.toggle(node.id)
      }
      // Затем установка/сброс active
      this.toggleActive(node)
    },

    // Логика поиска canonical элемента в landing (как в ElementRenderer)
    findElementById(id) {
      const landing = this.$store.getters['landing/landing'] || []
      const self = this
      function find(arr) {
        if (!Array.isArray(arr)) return null
        for (let item of arr) {
          if (!item) continue
          if (item.id === id) return item
          if (item.children?.data) {
            const f = find(item.children.data)
            if (f) return f
          }
          if (Array.isArray(item.data)) {
            const f = find(item.data)
            if (f) return f
          }
          if (Array.isArray(item.list)) {
            const f = find(item.list)
            if (f) return f
          }
        }
        return null
      }
      return find(landing)
    },

    // Toggle behavior: если клик на том же элементе — сбрасываем active (null),
    // иначе — ставим canonical элемент
    toggleActive(node) {
      if (!node) return
      const canonical = this.findElementById(node.id) || node
      const current = this.activeElement
      if (current && current.id === canonical.id) {
        // Сбрасываем активный элемент
        this.$store.dispatch('activeElement/UPDATE_ACTIVE_ELEMENT', null)
      } else {
        // Устанавливаем новый активный элемент
        this.$store.dispatch('activeElement/UPDATE_ACTIVE_ELEMENT', canonical)
      }
    },

    isActive(node) {
      return !!(this.activeElement && node && this.activeElement.id === node.id)
    },

    enter(el) {
      el.style.display = 'block'
      const h = el.scrollHeight
      el.style.height = '0px'
      el.style.opacity = '0'
      void el.offsetHeight
      el.style.transition = 'height 260ms ease, opacity 200ms ease'
      el.style.height = h + 'px'
      el.style.opacity = '1'
      function done() {
        el.style.height = 'auto'
        el.style.transition = ''
        el.removeEventListener('transitionend', done)
      }
      el.addEventListener('transitionend', done)
    },
    leave(el) {
      const h = el.scrollHeight
      el.style.height = h + 'px'
      el.style.opacity = '1'
      void el.offsetHeight
      el.style.transition = 'height 260ms ease, opacity 200ms ease'
      el.style.height = '0px'
      el.style.opacity = '0'
      function done() {
        el.style.display = 'none'
        el.style.transition = ''
        el.removeEventListener('transitionend', done)
      }
      el.addEventListener('transitionend', done)
    }
  }
}
</script>

<style scoped>
.tree-node {
  background-color: var(--grey-1);
  border-radius: 0.5rem;
}
.tree-accordion > .tree-node > .tree-header {
  background-color: var(--grey-1);
}
.tree-accordion > .tree-node {
  border-radius: 0.5rem;
  overflow: hidden;
}

.tree-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  padding: 0.5rem;
  background-color: #121212;
}


.toggle-indicator {
  width: 1.1rem;
  display: inline-flex;
  justify-content: center;
  font-size: 0.95rem;
  opacity: 0.9;
}
.node-main {
  display: flex;
  flex-direction: column;
}
.node-title {
  font-weight: 600;
  line-height: 1;
}
.node-sub {
  font-size: 0.78rem;
  opacity: 0.6;
}
.children-wrap {
  overflow: hidden;
}

.children-wrap > .tree-node > .tree-header {
  border-radius: 0;
}
.icon-wrapper {
  width: 1.25rem;
  height: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.icon {
  position: relative;
  width: 1rem;
  height: 1rem;
  transition: transform 0.3s ease;
}

.icon.open {
  transform: rotate(180deg);
}

.icon-line {
  position: absolute;
  background-color: currentColor;
  border-radius: 0.125rem;
  transition: all 0.3s cubic-bezier(0.68, -0.55, 0.27, 1.55);
}

.horizontal {
  top: 50%;
  left: 0;
  width: 100%;
  height: 0.125rem;
  transform: translateY(-50%);
}

.vertical {
  top: 0;
  left: 50%;
  width: 0.125rem;
  height: 100%;
  transform: translateX(-50%);
}

.icon.open .vertical {
  height: 0;
  transform: translateX(-50%) scaleY(0.5);
}

.collapse-container {
  transition: height 0.4s cubic-bezier(0.33, 1, 0.68, 1);
  overflow: hidden;
  will-change: height;
}

.collapse-content {
  padding-left: 1.5rem;
}

/* Остальные стили (как были) */
.folders__header {
  display: flex;
  gap: 0.75rem;
  cursor: pointer;
  align-items: center;
  user-select: none;
  padding: 0.625rem 0;
  transition: background-color 0.2s;
}

.folders__header:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.folders__category {
  overflow: scroll;
  height: 100%;
}

.folders {
  height: 89vh;
  overflow: hidden;
}

.folders__container {
  overflow-y: scroll;
  display: flex;
  flex-wrap: nowrap;
  gap: 1rem;
  flex-direction: column;
  height: 100%;
  padding-bottom: 0.5rem;
  cursor: grab;
}

.folders__container::-webkit-scrollbar {
  width: 0;
}

::-webkit-scrollbar {
  width: 0;
}

.folders__item {
  border-radius: 0.4rem;
  overflow: hidden;
  position: relative;
  height: fit-content;
  min-height: calc(12vw + 2rem);
}
</style>
